import { Link } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { brandSettingsProductsPath } from "@/routes"
import type { ProductsPagination } from "@/types/products"

/**
 * OKS-931 — Prev/next pager.
 *
 * ## Why not `ui/pagination` or `content/gallery-pagination.tsx` (UX §3.4)
 *
 * The gallery's copy lives under `content_gallery.pagination.*`, so importing
 * it would put gallery strings on a settings page; and its mobile branch is a
 * "Charger plus" button, so generalising it means hand-editing a shipped
 * shared component for no gain. A settings catalogue is SCANNED, not
 * navigated to page 7.
 *
 * This control is identical at every width, so it needs no `useIsMobile`
 * branch — one less breakpoint to get wrong.
 *
 * Disabled ends are `disabled` BUTTONS, not removed elements, so the control
 * keeps a stable shape and tab order across pages (UX §6).
 */
export default function ProductsPager({
  pagination,
  perPage,
  archivedView,
}: {
  pagination: ProductsPagination
  perPage: number
  archivedView: boolean
}) {
  const { t } = useTranslation()

  // Hidden entirely when there is nothing to page through.
  if (pagination.count === 0) return null

  const from = (pagination.page - 1) * perPage + 1
  const to = Math.min(pagination.page * perPage, pagination.count)

  const hrefFor = (page: number) =>
    brandSettingsProductsPath({
      page,
      ...(archivedView ? { archived: 1 } : {}),
    })

  return (
    <nav
      aria-label={t("settings.brand.products.pagination.aria")}
      className="flex w-full flex-col items-center gap-2 sm:flex-row sm:justify-between"
      data-testid="brand-products-pager"
    >
      {/* Below `sm` the summary moves to its own line ABOVE the buttons. */}
      <span className="text-muted-foreground order-first text-sm sm:order-none">
        {t("settings.brand.products.pagination.summary", {
          from,
          to,
          count: pagination.count,
        })}
      </span>

      <div className="flex w-full gap-2 sm:w-auto">
        <PagerButton
          href={pagination.prev === null ? null : hrefFor(pagination.prev)}
          label={t("settings.brand.products.pagination.prev")}
          testId="brand-products-pager-prev"
        />
        <PagerButton
          href={pagination.next === null ? null : hrefFor(pagination.next)}
          label={t("settings.brand.products.pagination.next")}
          testId="brand-products-pager-next"
        />
      </div>
    </nav>
  )
}

function PagerButton({
  href,
  label,
  testId,
}: {
  href: string | null
  label: string
  testId: string
}) {
  // ⚠️ `flex-1`, NOT `w-full`. Both buttons are children of one flex ROW below
  // `sm` (see the container above), and `w-full` resolves each to 100% of the
  // container with no shrink — so the row measures
  // `288 + 288 + 8 gap = 584` against a 288px container at 320px, and
  // `343 + 343 + 8 = 694` at 375px. Both were measured; the arithmetic matches
  // the observed `scrollWidth` exactly.
  //
  // `flex-1` makes them SHARE the row instead. `h-11` is untouched — that is
  // OKS-874's ratified 44px mobile tap-target floor.
  //
  // ⚠️ This defect is structurally invisible to Vitest: jsdom performs no
  // layout, so `scrollWidth` is always 0 and the identical overflow assertion
  // passes vacuously there. It exists only in a browser, which is why UX §4's
  // 375px/320px constraint is a Playwright row.
  const className = "h-11 flex-1 sm:h-9 sm:flex-none"

  if (href === null) {
    return (
      <Button
        variant="outline"
        className={className}
        disabled
        data-testid={testId}
      >
        {label}
      </Button>
    )
  }

  return (
    <Button
      variant="outline"
      className={className}
      asChild
      data-testid={testId}
    >
      <Link href={href}>{label}</Link>
    </Button>
  )
}
